import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '@/core/firebase';
import { useAuth } from '@/core/AuthContext';
import { Button } from '../components/ui';
import { ALERTS } from '../styles/classNames';

interface TrainerResult {
  trainerId: string;
  secretLoginToken: string;
  loginUrl: string;
}

export function AdminPanel() {
  const { user, userData, signOut } = useAuth();
  const [trainerName, setTrainerName] = useState('');
  const [trainerEmail, setTrainerEmail] = useState('');
  const [trainerResult, setTrainerResult] = useState<TrainerResult | null>(null);
  const [clientUserId, setClientUserId] = useState('');
  const [clientTrainerId, setClientTrainerId] = useState('');
  const [clientDisplayName, setClientDisplayName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    setError(null);
    setMessage(null);
  }, []);

  const handleCreateTrainer = async () => {
    setError(null);
    setMessage(null);
    setTrainerResult(null);
    try {
      const token = await user?.getIdToken();
      const res = await fetch(
        `${
          import.meta.env.VITE_FUNCTIONS_BASE_URL ||
          'https://us-central1-grounded-fitness.cloudfunctions.net'
        }/createTrainer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ name: trainerName, email: trainerEmail }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to create trainer');
      }
      const data = (await res.json()) as TrainerResult;
      setTrainerResult(data);
      setMessage('Trainer created. Copy the secret URL to share.');
      setTrainerName('');
      setTrainerEmail('');
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to create trainer');
      }
    }
  };

  const handleCreateClient = async () => {
    setError(null);
    setMessage(null);
    try {
      const token = await user?.getIdToken();
      const res = await fetch(
        `${
          import.meta.env.VITE_FUNCTIONS_BASE_URL ||
          'https://us-central1-grounded-fitness.cloudfunctions.net'
        }/createClient`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({
            userId: clientUserId,
            trainerId: clientTrainerId,
            displayName: clientDisplayName,
            email: clientEmail,
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to create client');
      }
      setMessage('Client linked to trainer.');
      setClientUserId('');
      setClientTrainerId('');
      setClientDisplayName('');
      setClientEmail('');
    } catch (err: any) {
      setError(err.message || 'Failed to create client');
    }
  };

  const promoteSelf = async () => {
    if (!user) return;
    setPromoting(true);
    setError(null);
    try {
      await setDoc(
        doc(db, 'users', user.uid),
        {
          role: 'admin',
          displayName: user.displayName || 'Admin',
          email: user.email || '',
        },
        { merge: true }
      );
      setMessage('Promoted to admin. Reload the page to use admin tools.');
    } catch (err: any) {
      setError(err.message || 'Failed to promote to admin');
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="min-h-screen p-6 text-slate-50">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-slate-400">Admin tools</p>
            <h1 className="text-3xl font-semibold">Seed data</h1>
          </div>
          <Button variant="secondary" onClick={signOut}>
            Sign out
          </Button>
        </div>

        {error && <div className={ALERTS.error}>{error}</div>}
        {message && <div className={ALERTS.success}>{message}</div>}

        {!userData?.role || userData.role !== 'admin' ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-5 border border-white/10 space-y-3"
          >
            <h2 className="text-xl font-semibold">Promote to admin (testing)</h2>
            <p className="text-sm text-slate-400">
              For quick testing, promote the current signed-in user to admin to unlock the seeding tools.
            </p>
            <Button onClick={promoteSelf} disabled={promoting} isLoading={promoting}>
              Make me admin
            </Button>
          </motion.div>
        ) : null}

        {userData?.role === 'admin' && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-5 border border-white/10 space-y-4"
          >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Create trainer</h2>
            <span className="text-sm text-slate-400">Calls createTrainer</span>
          </div>
          <div className="space-y-3">
            <input
              className="form-field"
              placeholder="Trainer name"
              value={trainerName}
              onChange={(e) => setTrainerName(e.target.value)}
            />
            <input
              className="form-field"
              placeholder="Trainer email (optional)"
              value={trainerEmail}
              onChange={(e) => setTrainerEmail(e.target.value)}
            />
            <Button onClick={handleCreateTrainer} disabled={!trainerName}>
              Create trainer
            </Button>
          </div>
          {trainerResult && (
            <div className="p-4 rounded-lg border border-white/10 bg-white/5 space-y-2 text-sm">
              <p>
                <span className="text-slate-400">Trainer ID:</span>{' '}
                {trainerResult.trainerId}
              </p>
              <p>
                <span className="text-slate-400">Secret token:</span>{' '}
                {trainerResult.secretLoginToken}
              </p>
              <p>
                <span className="text-slate-400">Login URL:</span>{' '}
                /t/{trainerResult.secretLoginToken}
              </p>
            </div>
          )}
          </motion.div>
        )}

        {userData?.role === 'admin' && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-5 border border-white/10 space-y-4"
          >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Link client to trainer</h2>
            <span className="text-sm text-slate-400">Calls createClient</span>
          </div>
          <div className="space-y-3">
            <input
              className="form-field"
              placeholder="Client userId (auth uid)"
              value={clientUserId}
              onChange={(e) => setClientUserId(e.target.value)}
            />
            <input
              className="form-field"
              placeholder="Trainer ID"
              value={clientTrainerId}
              onChange={(e) => setClientTrainerId(e.target.value)}
            />
            <div className="grid md:grid-cols-2 gap-3">
              <input
                className="form-field"
                placeholder="Client display name"
                value={clientDisplayName}
                onChange={(e) => setClientDisplayName(e.target.value)}
              />
              <input
                className="form-field"
                placeholder="Client email (optional)"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
              />
            </div>
            <Button onClick={handleCreateClient} disabled={!clientUserId || !clientTrainerId}>
              Link client
            </Button>
          </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
